import React, { useState, useEffect, useCallback } from "react";
import {
  PayPalScriptProvider,
  usePayPalCardFields,
  PayPalCardFieldsProvider,
  PayPalButtons,
  PayPalCardFieldsForm,
} from "@paypal/react-paypal-js";
import { useDispatch, useSelector } from 'react-redux';
import { paypalPaymentSuccess } from '../../../redux/actions/PaypalActions';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Reusable function to construct payload
const constructPayload = (checkoutData = null, orderId = null) => {
  if (!checkoutData) return null;
  // Extract and format section_data consistently
  const section_data = checkoutData.section_data || [];
  
  // For initial order creation
  if (!orderId) {
    return {
      amount: parseFloat(checkoutData.amount || "0").toFixed(2),
      currency: "usd",
      email: checkoutData.email,
      section_data: section_data,
      session_id: checkoutData.session_id,
      coupon_code: checkoutData.coupon_code || ''
    };
  }

  // For payment capture, return full payload
  return {
    first_name: checkoutData.firstName,
    middle_name: checkoutData.middleName,
    last_name: checkoutData.lastName,
    email: checkoutData.email,
    password: checkoutData.password,
    phone: checkoutData.phone,
    address: checkoutData.address,
    city: checkoutData.city,
    state: checkoutData.state,
    zipcode: checkoutData.zipCode,
    driving_license_number: checkoutData.dlNumber,
    dl_state: checkoutData.dlState,
    military_affiliation: checkoutData.affiliated,
    dob: checkoutData.dob,
    amount: parseFloat(checkoutData.amount || "0").toFixed(2),
    currency: "usd",
    payment_method_id: "paypal",
    section_data: section_data,
    order_id: orderId,
    session_id: checkoutData.session_id,
    coupon_code: checkoutData.coupon_code || ''
  };
};

export default function Paypal({ checkoutData, isFormValid }) {
  const [isPaying, setIsPaying] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isSuccess, paymentDetails } = useSelector(state => state.paypal);
  const coupon = useSelector(state => state.cart?.coupon);
  const { isUserLoggedIn } = useSelector(state => state.user);
  const queryParams = useSelector(state => state.wizardLaunch.queryParams);

  const initialOptions = {
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
    "disable-funding": "venmo,paylater,card",
    "buyer-country": "US",
    currency: "USD",
    components: "buttons,card-fields",
  };

  const [billingAddress, setBillingAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    adminArea1: "",
    adminArea2: "",
    countryCode: "",
    postalCode: "",
  });
  useEffect(() => {
    if (isSuccess && paymentDetails) {
      queryParams && successHubspotSendData();
      setTimeout(() => {
        navigate("/paymentsuccess", { replace: true });
      }, 100);
    }
  }, [isSuccess, paymentDetails, navigate]);
  const handleBillingAddressChange = useCallback((field, value) => {
    setBillingAddress((prev) => ({ ...prev, [field]: value }));
  }, []);

  const successHubspotSendData = useCallback(async () => {
    try {

     // Extract UTM parameters from queryParams
     const utmParams = {};
     Object.keys(queryParams).forEach(key => {
       if (key.startsWith('utm_')) {
         utmParams[key] = queryParams[key];
       }
     });
     if (Object.keys(utmParams).length > 0) {
       // Format UTM parameters as a query string
     const utmString = Object.keys(utmParams).length > 0 
     ? Object.entries(utmParams).map(([key, value]) => `${key}=${value}`).join('&')
     : ' ';
   
   const hubspotDataFormData = new FormData();
   const hubspotData = {
    firstname:  checkoutData.firstName,
    lastname: checkoutData.lastName,
    email: checkoutData.email,
    state: checkoutData.state,
    zip: checkoutData.zipCode,
    phone: checkoutData.phone,      
    affiliated_with_us_military: checkoutData.affiliated,
    lead_type: 'ELDT | Signup',
    utm: utmString
   };

   hubspotDataFormData.append("sentFormData", JSON.stringify(hubspotData));

   const hunspotresponse = await fetch(`${process.env.REACT_APP_SERVER_URL_FOR_CDL}/ws_hubspot_contact`, {
    method: 'POST',
    body: hubspotDataFormData,
   });
     }
    


    } catch (err) {
      throw err;
    }
  }, []);
  const createOrder = useCallback(async () => {
    try {
      const formData = new FormData();
      const payload = {
        ...constructPayload(checkoutData),
        amount: checkoutData.amount,
        coupon_code: coupon?.code || '',
        proceed: isUserLoggedIn
      };
      formData.append("sentFormData", JSON.stringify(payload));
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL_FOR_CDL}/app.php/ws_process_paypal_payment`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.status === 400) {
        throw new Error(data.message || "Payment failed");
      }
      if (data.response && data.response.id) {
        return data.response.id;
      } else {
        throw new Error(data.message || "Payment failed");
      }
    } catch (err) {
      throw err;
    }
  }, [checkoutData, coupon?.code]);

  const onApprove = useCallback(async (data) => {
    try {
      const formData = new FormData();
      const payload = {
        ...constructPayload(checkoutData, data.orderID),
        amount: checkoutData.amount,
        coupon_code: coupon?.code || ''
      };
      formData.append("sentFormData", JSON.stringify(payload));
  
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL_FOR_CDL}/app.php/ws_capture_paypal_payment`,
        {
          method: "POST",
          body: formData,
        }
      );
  
      const orderData = await response.json(); 
      if (orderData.status === 200) {
        const { user_id, email, created_at, session_id, redirect_url, jwt } = orderData;
        const transactionData = {
          status: orderData.status.toString(),
          user_id,
          email,
          amount: checkoutData.amount,
          created_at,
          session_id,
          redirect_url,
          jwt,
        };
        dispatch(paypalPaymentSuccess(transactionData));
      } else {
        const errorMessage = orderData.message || 
          (orderData.error && typeof orderData.error === 'object' ? 
            Object.values(orderData.error).join(', ') : 
            'Payment processing failed. Please try again.');
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error("Payment Error:", err);
      toast.error(err.message || "An unexpected error occurred during payment processing.");
      return false;
    }
  }, [checkoutData, coupon?.code, dispatch]);

  const onError = useCallback((error) => {
    toast.error(error.message);
  }, []);

  return (
    <div className="card_container">
      <PayPalScriptProvider options={initialOptions}>
        <PayPalButtons
          className="paypal-btn"
          createOrder={createOrder}
          onApprove={onApprove}
          onError={onError}
          disabled={!isFormValid}
          style={{
            shape: "pill",
            layout: "vertical",
            color: "gold",
            label: "paypal",
          }}
        />

        <PayPalCardFieldsProvider createOrder={createOrder} onApprove={onApprove}>
          <PayPalCardFieldsForm />
          {Object.keys(billingAddress).map((field) => (
            <input
              key={field}
              type="text"
              placeholder={field.replace(/([A-Z])/g, " $1").trim()}
              onChange={(e) => handleBillingAddressChange(field, e.target.value)}
            />
          ))}
          <SubmitPayment
            isPaying={isPaying}
            setIsPaying={setIsPaying}
            billingAddress={billingAddress}
          />
        </PayPalCardFieldsProvider>
      </PayPalScriptProvider>
    </div>
  );
}

const SubmitPayment = React.memo(({ isPaying, setIsPaying, billingAddress }) => {
  const { cardFieldsForm } = usePayPalCardFields();

  const handleClick = async () => {
    if (!cardFieldsForm) {
      throw new Error("Unable to find card fields form");
    }

    const formState = await cardFieldsForm.getState();
    if (!formState.isFormValid) {
      return alert("The payment form is invalid");
    }

    setIsPaying(true);
    cardFieldsForm.submit({ billingAddress }).finally(() => setIsPaying(false));
  };

  return (
    <button className={isPaying ? "btn" : "btn btn-primary"} onClick={handleClick}>
      {isPaying ? <div className="spinner tiny" /> : "Pay"}
    </button>
  );
});

const Message = React.memo(({ content }) => <p>{content}</p>);